import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import Barcode from 'react-barcode';
import { digitsOnly, validateEAN8, validateEAN13, validateUPC12 } from '../utils/barcodeUtils';
import StatusIndicator from './StatusIndicator';
import { resolveCardStatus } from '../utils/pidStatus';
import { commodityColor as colorForCommodity } from '../utils/commodityColor';
import {
  Overlay,
  Panel,
  Header,
  Title,
  CloseButton,
  CarouselWrapper,
  TwoColumnSection,
  LeftColumn,
  RightColumn,
  DetailGrid,
  BarcodeWrapper,
  InfoRow,
  InfoLabel,
  InfoValue,
  SectionDivider,
  CarouselNavButton,
} from '../styles/PackageIndexStyles';

const BASE_URL = 'https://thevaultmedia.azureedge.net/vault-images/PIDBags/';
const FALLBACK_IMAGE = `${BASE_URL}BagFallback.webp`;

/* ================== Pill styling ================== */
const PillGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CommodityPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--white);
  background: ${({ $bg }) => $bg};
  border: 2px solid ${({ $border }) => $border};
  box-shadow: 0 2px 6px var(--shadow-a15);
  user-select: none;
`;

const StylesWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const GlassChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 12px;
  background: var(--white-a40);
  color: var(--black);
  box-shadow: 0 2px 6px var(--shadow-a05);
  backdrop-filter: blur(8px);
  border: 1px solid var(--shadow-a05);
  white-space: nowrap;
  max-width: 100%;
`;


/* Color mapping: the commodity palette now lives in utils/commodityColor.js
   (imported above as colorForCommodity) so every surface shares one source. */

const normalizeImage = (img) =>
  img && typeof img === 'string'
    ? (img.startsWith('http') ? img : `${BASE_URL}${img}`)
    : null;

const BagDetailPanel = React.memo(({ group, onClose }) => {
  const panelRef = useRef();
  const { LabelID, Items = [], Images = [], Picture } = group;

  // Prefer resolved fields on first row for left column details
  const first = Items[0] || {};
  const ItemNumber = first.ItemNumber;
  const Retailer = first.Retailer;
  const Item_Description = first.Item_Description;
  const Type = first.Type;
  const UPC = first.UPC;

  // Build image list
  const imageList = useMemo(() => {
    const list = (Array.isArray(Images) ? Images : [])
      .map(normalizeImage)
      .filter(Boolean);
    if (list.length) return list;
    const pic = normalizeImage(Picture);
    return pic ? [pic] : [FALLBACK_IMAGE];
  }, [Images, Picture]);

  // Preload images
  useEffect(() => {
    imageList.forEach(src => { const i = new Image(); i.src = src; });
  }, [imageList]);

  const [current, setCurrent] = useState(0);
  const max = imageList.length;

  const swipeHandlers = useSwipeable({
    onSwipedLeft:  () => setCurrent(c => (c + 1) % max),
    onSwipedRight: () => setCurrent(c => (c - 1 + max) % max),
    trackMouse: true,
  });

  // Close on outside click or Escape
  const handleClickOutside = useCallback(
    e => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); },
    [onClose]
  );
  const handleEscape = useCallback(
    e => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [handleClickOutside, handleEscape]);

  // Group commodities to styles mapping
  const commodityToStyles = useMemo(() => {
    const map = new Map();
    (Items || []).forEach(({ CommodityName, Commodity, Style, ResolvedStyles }) => {
      const name = (CommodityName ?? Commodity) || '';
      if (!name) return;
      if (!map.has(name)) map.set(name, new Set());
      const stylesArray = Array.isArray(ResolvedStyles) && ResolvedStyles.length > 0
        ? ResolvedStyles
        : (Style ? [String(Style)] : []);
      for (const s of stylesArray) {
        if (s && String(s).trim()) map.get(name).add(String(s).trim());
      }
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
      .map(([name, set]) => ({ name, styles: Array.from(set).sort((a,b)=>a.localeCompare(b)) }));
  }, [Items]);

  // Validate UPC — only if there's a real value; otherwise show "No UPC"
  const { codeDigits, codeFormat } = useMemo(() => {
    const d = digitsOnly(UPC);
    if (d.length === 8 && validateEAN8(d)) return { codeDigits: d, codeFormat: 'EAN8' };
    if (d.length === 12 && validateUPC12(d)) return { codeDigits: d, codeFormat: 'UPC' };
    if (d.length === 13 && validateEAN13(d)) return { codeDigits: d, codeFormat: 'EAN13' };
    return { codeDigits: null, codeFormat: null };
  }, [UPC]);

  return (
    <Overlay>
      <Panel ref={panelRef} role="dialog" aria-modal="true">
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <Title>{LabelID}</Title>
            <StatusIndicator status={resolveCardStatus(group)} />
          </div>
          <CloseButton onClick={onClose}><X size={20} /></CloseButton>
        </Header>

        <TwoColumnSection>
          <LeftColumn>
            {/* Image carousel */}
            <CarouselWrapper {...swipeHandlers}>
              <div style={{ position: 'relative', width: '100%' }}>
                {max > 1 && current > 0 && (
                  <CarouselNavButton left onClick={() => setCurrent((current - 1 + max) % max)}>
                    &#8249;
                  </CarouselNavButton>
                )}
                {max > 1 && current < max - 1 && (
                  <CarouselNavButton right onClick={() => setCurrent((current + 1) % max)}>
                    &#8250;
                  </CarouselNavButton>
                )}
                <img
                  src={imageList[current]}
                  alt={LabelID}
                  onError={e => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
                  style={{ width: '100%', height: 'auto', maxHeight: 'min(40vh,300px)', objectFit: 'contain', borderRadius: '8px' }}
                />
              </div>
            </CarouselWrapper>

            {/* Details */}
            <DetailGrid>
              {Type && (
                <InfoRow>
                  <InfoLabel>Type</InfoLabel>
                  <InfoValue>{Type}</InfoValue>
                </InfoRow>
              )}
              {ItemNumber && (
                <InfoRow>
                  <InfoLabel>Item Number</InfoLabel>
                  <InfoValue>{ItemNumber}</InfoValue>
                </InfoRow>
              )}
              {Item_Description && (
                <InfoRow>
                  <InfoLabel>Item Description</InfoLabel>
                  <InfoValue>{Item_Description}</InfoValue>
                </InfoRow>
              )}
              {Retailer && (
                <InfoRow>
                  <InfoLabel>Retailer</InfoLabel>
                  <InfoValue>{Retailer}</InfoValue>
                </InfoRow>
              )}
              {codeDigits ? (
                <BarcodeWrapper>
                  <Barcode
                    value={codeDigits}
                    format={codeFormat}
                    height={60}
                    width={1.5}
                    displayValue
                    background="transparent"
                    font="OCRB"
                  />
                </BarcodeWrapper>
              ) : (
                <InfoRow>
                  <InfoLabel>No UPC</InfoLabel>
                </InfoRow>
              )}
            </DetailGrid>
          </LeftColumn>

          <SectionDivider />

          <RightColumn style={{ overflowY: 'auto', maxHeight: '70vh' }}>
            <PillGrid>
              {commodityToStyles.map(({ name, styles }) => {
                const { bg, border } = colorForCommodity(name);
                return (
                  <div key={name}>
                    <CommodityPill $bg={bg} $border={border}>{name}</CommodityPill>
                    {styles.length > 0 && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <StylesWrap>
                          {styles.map(style => (
                            <GlassChip key={`${name}-${style}`}>{style}</GlassChip>
                          ))}
                        </StylesWrap>
                      </div>
                    )}
                  </div>
                );
              })}
            </PillGrid>
          </RightColumn>
        </TwoColumnSection>
      </Panel>
    </Overlay>
  );
});

export default BagDetailPanel;
